import { Menu } from '@mantine/core';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ApplicationParams } from 'types/route_params';
import { Dashboard } from 'types/dashboard';
import { MonitorRoute, NavRoute } from 'application/types/routes';

import { exportDashboardAsJson } from 'dashboards/services/dashboard';
import { useCreateDashboard } from 'dashboards/hooks/useCreateDashboard';
import { useDeleteDashboard } from 'dashboards/hooks/useDeleteDashboard';
import { useModal } from 'hooks/useModal';
import { useTagsFilterParams } from 'tags/hooks/useTagsFilterParams';

import { Icon } from 'components/Icon/Icon';
import { IconButton } from 'components/IconButton/IconButton';
import { SidebarLink } from 'components/Sidebar/SidebarLink';

type DashboardLinkProps = {
  dashboard: Dashboard;
};

export function DashboardLink({ dashboard }: DashboardLinkProps) {
  const navigate = useNavigate();

  const { applicationID } = useParams<ApplicationParams>();
  const { open } = useModal();
  const { tagged_with } = useTagsFilterParams();

  const [searchParams] = useSearchParams();

  const updatedSearchParams = new URLSearchParams(searchParams);

  if (tagged_with?.length) {
    updatedSearchParams.set('tags', tagged_with.join(','));
  }

  const search = updatedSearchParams.toString();

  const [requestDeleteDashboard] = useDeleteDashboard({
    onSuccess() {
      navigate({
        pathname: `/applications/${applicationID}/${NavRoute.MONITOR}/dashboards`,
        search,
      });
    },
  });

  function handleDeleteClick() {
    requestDeleteDashboard(dashboard.id);
  }

  const { mutate: createDashboard } = useCreateDashboard({
    onSuccess(dashboard) {
      navigate({
        pathname: `/applications/${applicationID}/${NavRoute.MONITOR}/${MonitorRoute.DASHBOARDS}/${dashboard.id}`,
        search,
      });
    },
  });

  function handleDuplicateClick() {
    createDashboard({
      name: `Copy of ${dashboard.name}`,
      configuration: dashboard.configuration,
    });
  }

  return (
    <SidebarLink
      to={{
        pathname: `/${NavRoute.MONITOR}/${MonitorRoute.DASHBOARDS}/${dashboard.id}`,
        search,
      }}
      action={
        <Menu>
          <Menu.Target>
            <IconButton
              size="small"
              icon="more-vertical"
              label={`More options for ${dashboard.name}`}
            />
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              onClick={() => open('rename-dashboard', { dashboard })}
              leftSection={<Icon name="settings" size="small" />}
            >
              Rename
            </Menu.Item>
            <Menu.Item
              onClick={handleDuplicateClick}
              leftSection={<Icon name="copy" size="small" />}
            >
              Duplicate
            </Menu.Item>
            <Menu.Item
              onClick={() => exportDashboardAsJson(dashboard)}
              leftSection={<Icon name="export" size="small" />}
            >
              Export as JSON
            </Menu.Item>

            <Menu.Divider />

            <Menu.Item
              color="red"
              onClick={handleDeleteClick}
              leftSection={<Icon name="delete" size="small" unstyled />}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      }
    >
      <span>{dashboard.name}</span>
    </SidebarLink>
  );
}
